import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BranchDto, PurchaseItemRow, PurchaseItemsReport, ReportExportRequest } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { KpiCard } from '@/components/KpiCard';
import { ExportMenu } from '@/components/ExportMenu';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { fetchPurchaseItems, purchaseItemsKeys } from '@/features/reports/purchase-items-api';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { buildExportTable, exportSubtitle } from '@/features/reports/export-table';
import type { ExportColumnSpec } from '@/features/reports/export-table';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const qty = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, ''));
const sum = <T,>(rows: readonly T[], pick: (row: T) => number) => rows.reduce((acc, row) => acc + pick(row), 0);

const TOGGLEABLE = [
  'quantity', 'purchases', 'totalCost', 'avgCost', 'lastPurchaseDate', 'lastCost', 'suppliers',
  'returnedQuantity', 'returnedValue', 'netQuantity', 'netCost',
] as const;

const LABELS: Record<string, string> = {
  quantity: 'Qty bought',
  purchases: 'Purchases',
  totalCost: 'Total cost',
  avgCost: 'Avg cost',
  lastPurchaseDate: 'Last purchased',
  lastCost: 'Last cost',
  suppliers: 'Suppliers',
  returnedQuantity: 'Qty returned',
  returnedValue: 'Returned value',
  netQuantity: 'Net qty',
  netCost: 'Net cost',
};

const COLUMNS: Column<PurchaseItemRow>[] = [
  { key: 'productName', header: 'Product', pinned: true, cell: (row) => row.productName },
  { key: 'quantity', header: 'Qty bought', align: 'right', cell: (row) => qty(row.quantity) },
  { key: 'purchases', header: 'Purchases', align: 'right', cell: (row) => String(row.purchases) },
  { key: 'totalCost', header: 'Total cost', align: 'right', cell: (row) => money(row.totalCost) },
  { key: 'avgCost', header: 'Avg cost', align: 'right', cell: (row) => money(row.avgCost) },
  { key: 'lastPurchaseDate', header: 'Last purchased', cell: (row) => row.lastPurchaseDate || '—' },
  { key: 'lastCost', header: 'Last cost', align: 'right', cell: (row) => (row.lastPurchaseDate ? money(row.lastCost) : '—') },
  {
    key: 'suppliers',
    header: 'Suppliers',
    visibility: 'hidden md:table-cell',
    cell: (row) => (row.suppliers.length ? row.suppliers.join(', ') : '—'),
  },
  { key: 'returnedQuantity', header: 'Qty returned', align: 'right', cell: (row) => qty(row.returnedQuantity) },
  { key: 'returnedValue', header: 'Returned value', align: 'right', cell: (row) => money(row.returnedValue) },
  { key: 'netQuantity', header: 'Net qty', align: 'right', cell: (row) => qty(row.netQuantity) },
  { key: 'netCost', header: 'Net cost', align: 'right', cell: (row) => money(row.netCost) },
];

/** Plain-text twins of COLUMNS, keyed the same so the Columns layout applies. */
const EXPORT_COLUMNS: ExportColumnSpec<PurchaseItemRow>[] = [
  { key: 'productName', header: 'Product', width: 3, text: (row) => row.productName },
  { key: 'quantity', header: 'Qty bought', align: 'right', text: (row) => qty(row.quantity) },
  { key: 'purchases', header: 'Purchases', align: 'right', numeric: true, text: (row) => String(row.purchases) },
  { key: 'totalCost', header: 'Total cost', align: 'right', money: true, text: (row) => money(row.totalCost) },
  { key: 'avgCost', header: 'Avg cost', align: 'right', money: true, text: (row) => money(row.avgCost) },
  { key: 'lastPurchaseDate', header: 'Last purchased', text: (row) => row.lastPurchaseDate || '—' },
  {
    key: 'lastCost',
    header: 'Last cost',
    align: 'right',
    money: true,
    text: (row) => (row.lastPurchaseDate ? money(row.lastCost) : '—'),
  },
  { key: 'suppliers', header: 'Suppliers', width: 2, text: (row) => (row.suppliers.length ? row.suppliers.join(', ') : '—') },
  { key: 'returnedQuantity', header: 'Qty returned', align: 'right', text: (row) => qty(row.returnedQuantity) },
  { key: 'returnedValue', header: 'Returned value', align: 'right', money: true, text: (row) => money(row.returnedValue) },
  { key: 'netQuantity', header: 'Net qty', align: 'right', text: (row) => qty(row.netQuantity) },
  { key: 'netCost', header: 'Net cost', align: 'right', money: true, text: (row) => money(row.netCost) },
];

/** The table as shown (search applied), with the same footer totals. */
function purchaseItemsTable(
  rows: readonly PurchaseItemRow[],
  visibleKeys: readonly string[],
  filters: ReportFilters,
  supplier: string,
  branches: readonly BranchDto[],
): ReportExportRequest {
  const subtitle = exportSubtitle(filters, { branchId: filters.branchId, branches });
  return buildExportTable({
    title: 'Item-wise purchases',
    subtitle: supplier ? `${subtitle} · Supplier: ${supplier}` : subtitle,
    from: filters.from,
    to: filters.to,
    columns: EXPORT_COLUMNS,
    visibleKeys,
    rows,
    totals: {
      label: 'Total',
      cells: {
        quantity: qty(sum(rows, (r) => r.quantity)),
        totalCost: money(sum(rows, (r) => r.totalCost)),
        returnedQuantity: qty(sum(rows, (r) => r.returnedQuantity)),
        returnedValue: money(sum(rows, (r) => r.returnedValue)),
        netQuantity: qty(sum(rows, (r) => r.netQuantity)),
        netCost: money(sum(rows, (r) => r.netCost)),
      },
    },
  });
}

/**
 * What was bought, per product, across every till: pieces, bills, cost,
 * average and latest cost, and suppliers — less purchase returns in the
 * same window. The endpoint returns the whole window; search runs here.
 */
export function PurchaseItemsPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [supplier, setSupplier] = useState('');
  const [search, setSearch] = useState('');
  const layout = useReportLayout('purchase-items', TOGGLEABLE);
  const columns = applyReportLayout(COLUMNS, layout);

  const query = useQuery({
    queryKey: purchaseItemsKeys.report(filters, supplier),
    queryFn: ({ signal }) => fetchPurchaseItems(filters, supplier, signal),
    // Keeps the supplier picker filled while another supplier's figures load.
    placeholderData: keepPreviousData,
  });
  const suppliers = query.data?.suppliers ?? [];
  const whole = query.data;

  const select = useMemo(
    () =>
      (report: PurchaseItemsReport): PurchaseItemsReport => ({
        ...report,
        rows: applyClientFilters(report.rows, {
          search,
          filterRows: [],
          matchAny: true,
          fieldValue: () => undefined,
          searchValues: (row) => [row.productName, ...row.suppliers],
        }),
      }),
    [search],
  );

  const onExport = (format: ExportFormat) =>
    whole
      ? downloadReportExport(
          purchaseItemsTable(
            select(whole).rows,
            columns.map((column) => column.key),
            filters,
            supplier,
            profile?.branches ?? [],
          ),
          format,
        )
      : Promise.resolve();

  return (
    <div>
      <PageHeader
        title="Item-wise purchases"
        description="Per product: pieces bought, cost and suppliers, less purchase returns in the same window."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <ReportToolbar>
        <ReportSearchField value={search} onChange={setSearch} placeholder="Search product or supplier" />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span className="sr-only sm:not-sr-only">Supplier</span>
          <select
            className="input h-9 py-1 pr-9"
            value={supplier}
            onChange={(event) => setSupplier(event.target.value)}
            aria-label="Supplier"
          >
            <option value="">All suppliers</option>
            {supplier && !suppliers.includes(supplier) ? <option value={supplier}>{supplier}</option> : null}
            {suppliers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <ReportColumnsButton layout={layout} labels={LABELS} />
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={(range) => setFilters({ from: range.from, to: range.to })}
        />
        {whole && whole.rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        select={select}
        isEmpty={(report) => report.rows.length === 0}
        emptyTitle="No purchases in this window"
        skeleton={<TableSkeleton columns={12} />}
      >
        {(report) => (
          <>
            {whole ? (
              // Whole-window figures (search narrows only the table below).
              <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
                <KpiCard label="Products" value={String(whole.rows.length)} />
                <KpiCard label="Purchases" value={String(whole.totals.purchases)} />
                <KpiCard label="Total cost" value={money(whole.totals.totalCost)} />
                <KpiCard label="Returned" value={money(whole.totals.returnedValue)} />
                <KpiCard label="Net cost" value={money(whole.totals.netCost)} />
              </div>
            ) : null}
            <DataTable
              caption="Item-wise purchases"
              layout={layout}
              columns={columns}
              rows={report.rows}
              rowKey={(row) => row.productName}
              totals={{
                label: 'Total',
                cells: {
                  quantity: qty(sum(report.rows, (r) => r.quantity)),
                  totalCost: money(sum(report.rows, (r) => r.totalCost)),
                  returnedQuantity: qty(sum(report.rows, (r) => r.returnedQuantity)),
                  returnedValue: money(sum(report.rows, (r) => r.returnedValue)),
                  netQuantity: qty(sum(report.rows, (r) => r.netQuantity)),
                  netCost: money(sum(report.rows, (r) => r.netCost)),
                },
              }}
            />
          </>
        )}
      </AsyncSection>
    </div>
  );
}
